import { Component, OnInit, ViewChild, ElementRef, OnDestroy, HostListener, Input, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

interface CapturedPhoto {
  id: string;
  dataUrl: string;
  timestamp: Date;
  sizeBytes: number;
}

// jsPDF embeds the JPEG bytes as-is, so the photos dominate the file size. This covers the
// per-page structure and the timestamp line on top of them.
const PDF_OVERHEAD_BYTES_PER_PAGE = 2048;

@Component({
  selector: 'cpf-camera-photo',
  templateUrl: './camera-photo.component.html',
  styleUrls: ['./camera-photo.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CameraPhotoComponent implements OnInit, OnDestroy {
  @ViewChild('video', { static: false }) video!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: false }) canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('photosSection', { static: false }) photosSection!: ElementRef<HTMLElement>;

  /** Upload budget the generated PDF has to stay within. Null means no limit. */
  @Input() maxSizeBytes: number | null = null;

  stream: MediaStream | null = null;
  capturedPhotos: CapturedPhoto[] = [];
  isVideoReady = false;
  isCameraActive = false;
  error: string | null = null;
  isInitializing = true;
  isStartingCamera = false;
  // Drives the shutter flash. Capture is instantaneous and otherwise silent, which reads as
  // "nothing happened" and gets the button pressed repeatedly.
  isFlashing = false;

  private videoDevices: MediaDeviceInfo[] = [];
  private activeDeviceId: string | null = null;
  private switchCameraTimeout: ReturnType<typeof setTimeout> | null = null;
  private flashTimeout: ReturnType<typeof setTimeout> | null = null;
  private isDestroyed = false;

  constructor(private translate: TranslateService) {}

  get hasMultipleCameras(): boolean {
    return this.videoDevices.length > 1;
  }

  // Estimated up front from the captured JPEGs so the user finds out here, while the photos
  // still exist and can be pruned - rather than after the modal has closed and the host form
  // rejects the finished PDF.
  get lastPhoto(): CapturedPhoto | undefined {
    return this.capturedPhotos[this.capturedPhotos.length - 1];
  }

  // The captured-photos list sits below the camera and is off-screen on a phone, so the
  // thumbnail next to the shutter doubles as the way to reach it.
  scrollToPhotos() {
    this.photosSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  get estimatedPdfSizeBytes(): number {
    return this.capturedPhotos.reduce((total, photo) => total + photo.sizeBytes, 0)
      + (this.capturedPhotos.length * PDF_OVERHEAD_BYTES_PER_PAGE);
  }

  get isOverSizeBudget(): boolean {
    return this.maxSizeBytes !== null && this.estimatedPdfSizeBytes > this.maxSizeBytes;
  }

  get estimatedPdfSizeLabel(): string {
    return this.formatMegabytes(this.estimatedPdfSizeBytes);
  }

  get maxSizeLabel(): string {
    return this.maxSizeBytes === null ? '' : this.formatMegabytes(this.maxSizeBytes);
  }

  private formatMegabytes(bytes: number): string {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private estimateDataUrlBytes(dataUrl: string): number {
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    return Math.max(0, (base64.length * 3) / 4 - padding);
  }

  async ngOnInit() {
    try {
      await this.startCamera();
    } finally {
      // Ensure isInitializing is always reset, even if there's an error
      this.isInitializing = false;
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.cleanupCamera();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: any) {
    this.cleanupCamera();
  }

  @HostListener('window:unload', ['$event'])
  handleUnload(event: any) {
    this.cleanupCamera();
  }

  // Browsers only expose the real camera list (ids and labels) once camera permission has been
  // granted - before that Safari and Firefox report a single generic device. So this has to run
  // *after* getUserMedia succeeds, otherwise multi-camera devices look single-camera to us.
  private async refreshVideoDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.videoDevices = devices.filter(device => device.kind === 'videoinput');
    } catch (error) {
      console.error('Error checking for multiple cameras:', error);
      this.videoDevices = [];
    }
  }

  async startCamera(deviceId?: string) {
    try {
      this.error = null;
      this.isStartingCamera = true;
      this.isVideoReady = false;

      // Clean up any existing stream first
      this.cleanupStream();

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          // An explicit device wins when the user is cycling cameras; otherwise prefer the
          // back camera on mobile.
          ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' }),
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      // getUserMedia can resolve after the modal has been closed; without this the freshly
      // acquired stream would never be stopped and the camera would stay on.
      if (this.isDestroyed) {
        this.cleanupStream();
        return;
      }

      this.activeDeviceId = this.stream.getVideoTracks()[0]?.getSettings().deviceId ?? deviceId ?? null;
      await this.refreshVideoDevices();

      if (this.video && this.video.nativeElement) {
        this.video.nativeElement.srcObject = this.stream;
        this.video.nativeElement.onloadedmetadata = () => {
          this.isVideoReady = true;
          this.isCameraActive = true;
          this.isStartingCamera = false;
        };
        
        // Add error handler for video element
        this.video.nativeElement.onerror = (error) => {
          console.error('Video element error:', error);
          this.handleCameraError(this.translate.instant('camera-photo.errors.video-playback-error'));
        };
      } else {
        // No preview to attach the stream to - surface it instead of leaving the user on an
        // endless "starting camera" spinner with the camera silently running.
        this.handleCameraError(this.translate.instant('camera-photo.errors.video-playback-error'));
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      this.handleCameraError(this.translate.instant('camera-photo.errors.camera-access-denied'));
    }
  }

  private handleCameraError(errorMessage: string) {
    this.error = errorMessage;
    this.isCameraActive = false;
    this.isStartingCamera = false;
    this.isVideoReady = false;
    
    // Clean up any partially created stream
    this.cleanupStream();
  }

  stopCamera() {
    this.isVideoReady = false;
    this.isCameraActive = false;
    this.cleanupStream();
  }

  private cleanupStream() {
    if (this.stream) {
      try {
        // Stop all tracks (video and audio if any)
        this.stream.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
        
        // Clear video source
        if (this.video && this.video.nativeElement) {
          this.video.nativeElement.srcObject = null;
        }
      } catch (error) {
        console.error('Error stopping camera stream:', error);
      } finally {
        this.stream = null;
      }
    }
  }

  private cleanupCamera() {
    try {
      if (this.switchCameraTimeout) {
        clearTimeout(this.switchCameraTimeout);
        this.switchCameraTimeout = null;
      }

      if (this.flashTimeout) {
        clearTimeout(this.flashTimeout);
        this.flashTimeout = null;
        this.isFlashing = false;
      }

      this.cleanupStream();
      this.isVideoReady = false;
      this.isCameraActive = false;
      this.isStartingCamera = false;
      this.isInitializing = false;
    } catch (error) {
      console.error('Error during camera cleanup:', error);
    }
  }

  capturePhoto() {
    if (!this.video || !this.canvas || !this.isVideoReady) {
      return;
    }

    const video = this.video.nativeElement;
    const canvas = this.canvas.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    // Set canvas dimensions to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to data URL
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

    // Add captured photo to array
    const photo: CapturedPhoto = {
      id: Date.now().toString(),
      dataUrl: dataUrl,
      timestamp: new Date(),
      // Measured once here; recomputing it from the data URL on every change-detection run
      // would mean re-scanning every captured image.
      sizeBytes: this.estimateDataUrlBytes(dataUrl)
    };

    this.capturedPhotos.push(photo);
    this.triggerFlash();
  }

  private triggerFlash() {
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
    }

    this.isFlashing = true;
    this.flashTimeout = setTimeout(() => {
      this.flashTimeout = null;
      this.isFlashing = false;
    }, 220);
  }

  deletePhoto(photoId: string) {
    this.capturedPhotos = this.capturedPhotos.filter(photo => photo.id !== photoId);
  }

  clearAllPhotos() {
    this.capturedPhotos = [];
  }

  // Cycles through the available cameras by device id. Restarting with the same `facingMode`
  // hint would just reopen the camera we are already on, so the next device has to be picked
  // explicitly.
  switchCamera() {
    if (!this.hasMultipleCameras) {
      return;
    }

    const currentIndex = this.videoDevices.findIndex(device => device.deviceId === this.activeDeviceId);
    const nextDevice = this.videoDevices[(currentIndex + 1) % this.videoDevices.length];

    // Clean up current camera before switching
    this.cleanupStream();
    this.isVideoReady = false;
    this.isCameraActive = false;

    // Small delay to ensure cleanup is complete
    this.switchCameraTimeout = setTimeout(() => {
      this.switchCameraTimeout = null;
      this.startCamera(nextDevice.deviceId);
    }, 100);
  }

  trackByPhotoId(index: number, photo: CapturedPhoto): string {
    return photo.id;
  }

  async generatePDFBlob(): Promise<Blob> {
    if (this.capturedPhotos.length === 0) {
      throw new Error(this.translate.instant('camera-photo.alerts.no-photos-for-pdf'));
    }

    try {
      const pdf = await this.createPDFFromPhotos();
      return pdf.output('blob');
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error(this.translate.instant('camera-photo.alerts.pdf-generation-error'));
    }
  }

  private formatCaptureCaption(timestamp: Date): string {
    const lang = this.translate.getCurrentLang() || this.translate.getFallbackLang();
    // Intl works off the tag directly, so this needs no registered Angular locale data.
    const formatted = lang ? timestamp.toLocaleString(lang) : timestamp.toLocaleString();

    return this.translate.instant('camera-photo.pdf.captured-at', { timestamp: formatted });
  }

  private async createPDFFromPhotos(): Promise<any> {
    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    // Reserve a band at the bottom for the timestamp. Without it a full-height photo is
    // centred over the whole page and the caption gets stamped on top of the image.
    const footerHeight = 8;
    const availableWidth = pageWidth - (2 * margin);
    const availableHeight = pageHeight - (2 * margin) - footerHeight;

    for (let i = 0; i < this.capturedPhotos.length; i++) {
      const photo = this.capturedPhotos[i];
      
      if (i > 0) {
        pdf.addPage();
      }

      // Create image element to get dimensions
      const img = new Image();

      // Without an error handler a photo that fails to decode would leave this promise
      // pending forever, hanging the whole "generate PDF" action with no way out.
      const decoded = new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error(`Could not decode captured photo ${photo.id}`));
      });

      img.src = photo.dataUrl;
      await decoded;

      // Calculate dimensions to fit within page while maintaining aspect ratio
      const aspectRatio = img.width / img.height;
      let imgWidth = availableWidth;
      let imgHeight = availableWidth / aspectRatio;

      if (imgHeight > availableHeight) {
        imgHeight = availableHeight;
        imgWidth = availableHeight * aspectRatio;
      }

      // Center the image within the content area above the footer band
      const xPosition = (pageWidth - imgWidth) / 2;
      const yPosition = margin + ((availableHeight - imgHeight) / 2);

      pdf.addImage(photo.dataUrl, 'JPEG', xPosition, yPosition, imgWidth, imgHeight);
      
      // Add timestamp, in the language the user is running the portal in rather than the
      // browser's own locale
      pdf.setFontSize(8);
      pdf.text(this.formatCaptureCaption(photo.timestamp), margin, pageHeight - 5);
    }

    return pdf;
  }
}
